import type {
  Category,
  Dashboard,
  DashboardBody,
  DashboardFilter,
  InstallRequest,
  Listing,
  ListingDetail,
  ListingKind,
  ListingSort,
  PublishRequestInput,
  Principal,
  ResultEnvelope,
  SemanticQueryInput,
  ShareStatus,
  SidebarFolder,
  ShareTarget,
  TimePreset,
  UpdateCopyRequest,
  UpdatePreview,
  UpdateTarget,
  VizType,
  Widget,
  WidgetDefinitionInput,
} from "@cd/core";

/** Mirrors @cd/semantic's QueryOrigin: which widget a query is for, for the worker's query logs. */
export interface QueryOrigin { source: "widget" | "builder" | "breakdown"; widgetId?: string }

// Mirrors @cd/semantic's Catalog (kept structural so the web bundle doesn't pull in the planner).
export interface CatalogMetric {
  ref: string; label: string; description: string; model: string; modelLabel: string;
  agg: string; format?: ResultEnvelope["columns"][number]["format"]; additivity: string; certified: boolean; owner: string;
  changes: { version: string; date: string; note: string }[];
}
export interface CatalogDimension {
  ref: string; label: string; description?: string; model: string; modelLabel: string;
  type: "string" | "number" | "boolean" | "time"; grains?: string[];
  /** The shared dimension this one is the same as: shown only where a widget already uses it. */
  sameAs?: string;
  /** Other models' dimensions are the same as this one: listed under Shared rather than its model. */
  shared?: boolean;
}
export interface Catalog {
  version: string;
  models: { name: string; label: string }[];
  metrics: CatalogMetric[];
  dimensions: CatalogDimension[];
  compatibility: Record<string, string[]>;
}

/** One of the workspace's data packs: its catalog, the name a query's `pack` uses, and its label. */
export interface PackCatalog extends Catalog {
  name: string;
  label: string;
}

/** `/api/catalog`: every data pack in the workspace, first one first. Refs are only unique within a pack. */
export interface WorkspaceCatalog {
  packs: PackCatalog[];
}

export interface ExplainResult {
  columns: ResultEnvelope["columns"];
  warnings: ResultEnvelope["warnings"];
  provenance: ResultEnvelope["provenance"];
  certified: boolean;
}

export type DashboardWithMeta = Dashboard;
export interface DashboardDetail {
  dashboard: DashboardWithMeta;
  widgets: Record<string, Widget>;
  canEdit: boolean;
  /** Who it's shared with; only sent to the owner. */
  shares?: ShareTarget[];
}

export interface DirectoryPerson {
  id: string;
  name: string;
  email?: string;
  staff: boolean;
}

export interface ListingPage {
  results: Listing[];
  page: number;
  hasMore: boolean;
}

export interface MarketplaceSearch {
  q?: string;
  category?: Category;
  tag?: string;
  kind?: ListingKind;
  sort?: ListingSort;
  page?: number;
}

export interface MarketplaceFacets {
  categories: { id: Category; label: string; count: number }[];
  tags: { tag: string; n: number }[];
}

export interface MyListing extends Listing {
  /** Installs per day (UTC), last 30 days; days without installs are omitted. */
  trend: { day: string; installs: number }[];
}

export interface NlResult {
  query: SemanticQueryInput;
  title: string;
  visualization: VizType;
  assumptions: string[];
}

/** A question one chart can't answer: why, and chart questions to ask instead. */
export interface NotAChart {
  notAChart: true;
  reason: string;
  suggestions: string[];
}

/** `/me/ask-usage`: your own asks and tokens (all four counts), in UTC calendar months. */
export interface AskUsage {
  thisMonth: { asks: number; tokens: number };
  lastMonth: { asks: number; tokens: number };
}

/** Which sign-in a location pick completes: GoTab staff (cf_sso) or a GoTab operator (gotab.io). */
export type SignInVia = "staff" | "gotab";

/** A GoTab operator's org is just themselves (the worker's auth.ts). */
export const isGotabOperator = (p: Principal) => p.orgId.startsWith("gotab-user:");

/** While GoTab staff view as someone (the worker's access.ts): who is looking, and how. */
export interface ViewAsInfo {
  by: { name: string; email?: string };
  tier: "support" | "super";
  kind: "customer" | "staff";
  editing: boolean;
  expiresAt: string;
}

/** `/me`: who the app is showing. Staff who can view as others get their tier; while they do, `viewAs`. */
export type Me = Principal & {
  viewAs?: ViewAsInfo;
  viewAsTier?: ViewAsInfo["tier"];
  /** Staff, or a customer a super user turned Ask on for. Follows whoever is signed in. */
  canAsk?: boolean;
};

export interface ViewAsUser {
  id: string;
  name: string;
  email?: string;
  kind: ViewAsInfo["kind"];
  locationCount: number;
  lastSeenAt: string;
}

export type ViewAsAction = "start" | "enable_edit" | "write" | "exit";

/** A row of the view-as audit log (super users only; the worker's view_as_events). */
export interface ViewAsEvent {
  id: string;
  seq: number;
  viewAsId: string;
  staffId: string;
  staffEmail: string | null;
  tier: ViewAsInfo["tier"];
  targetUserId: string;
  targetName: string | null;
  targetKind: ViewAsInfo["kind"];
  action: ViewAsAction;
  /** start: {locationIds, expiresAt}; enable_edit: {reason}; write: {method, path, status?}; exit: null. */
  detail: { locationIds?: string[]; expiresAt?: string; reason?: string; method?: string; path?: string; status?: number } | null;
  at: string;
}

/** Audit log filters. `from`/`to` are ISO timestamps, half-open. */
export interface ViewAsEventSearch {
  staff?: string;
  target?: string;
  viewAsId?: string;
  action?: ViewAsAction;
  tier?: ViewAsInfo["tier"];
  targetKind?: ViewAsInfo["kind"];
  from?: string;
  to?: string;
}

/** A row of the download audit log (super users only; the worker's download_events). */
export interface DownloadEvent {
  id: string;
  seq: number;
  /** Whose access the data came from. */
  userId: string;
  userName: string | null;
  userEmail: string | null;
  orgId: string;
  /** Who was signed in: the same person, or staff viewing as them. */
  actorId: string;
  actorEmail: string | null;
  viewAsId: string | null;
  dashboardId: string | null;
  widgetId: string;
  widgetTitle: string;
  format: "xlsx" | "csv";
  rowCount: number;
  detail: { vizType?: string; locationIds?: string[]; timeRange?: { start: string; end: string }; filters?: string[] } | null;
  at: string;
}

/** Download log filters. `from`/`to` are ISO timestamps, half-open. */
export interface DownloadEventSearch {
  user?: string;
  actor?: string;
  widgetId?: string;
  viewAsId?: string;
  format?: DownloadEvent["format"];
  from?: string;
  to?: string;
}

/** One month's Ask usage for an org, or for one person within it (super users only; the worker's llm_usage). */
export interface LlmUsageTotal {
  month: string;
  orgId: string;
  /** Set when broken down by person: whoever asked (staff during a view-as stay themselves). */
  userId?: string;
  userName?: string;
  userEmail?: string;
  staff?: boolean;
  asks: number;
  calls: number;
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  cacheWriteTokens: number;
}

/** Usage view filters. `from`/`to` are ISO timestamps, half-open. Staff are left out unless asked for. */
export interface LlmUsageSearch {
  from?: string;
  to?: string;
  orgId?: string;
  userId?: string;
  staff?: "include" | "only";
}

/** Someone's Ask setting, with their own asks and tokens this calendar month (UTC). */
export interface AskAccess {
  userId: string;
  orgId: string;
  userName?: string;
  userEmail?: string;
  enabled: boolean;
  updatedBy: string;
  updatedAt: string;
  thisMonth: { asks: number; tokens: number };
}

/** A customer found by the Ask tab's search. */
export interface AskCandidate {
  userId: string;
  orgId: string;
  name: string;
  email?: string;
  locationCount: number;
  lastSeenAt: string;
  enabled: boolean;
}

export interface AskAccessDetail {
  userId: string;
  enabled: boolean;
  access: AskAccess | null;
  changes: { id: string; enabled: boolean; changedBy: string; changedByEmail?: string; at: string }[];
}

/** What the browser reports when it builds a download; the worker adds who made it. */
export interface DownloadReport {
  widgetId: string;
  dashboardId?: string;
  title: string;
  format: DownloadEvent["format"];
  rowCount: number;
  vizType: string;
  locationIds?: string[];
  timeRange?: { start: string; end: string };
  /** Every filter the result was built with, as Show your work words them. */
  filters?: string[];
}

/** When a scheduled report sends (the worker's cadence.ts). `weekday` 0 is Sunday. */
export type Cadence =
  | { kind: "daily"; hour: number }
  | { kind: "weekly"; weekday: number; hour: number }
  | { kind: "monthly"; day: number; hour: number };

/** A schedule's file(s): a spreadsheet, the printed dashboard, or both on one email. */
export type ScheduleFormat = "xlsx" | "csv" | "pdf" | "pdf+xlsx";

/** What a format is called in the UI. */
export const FORMAT_LABELS: Record<ScheduleFormat, string> = { xlsx: "Excel", csv: "CSV", pdf: "PDF", "pdf+xlsx": "PDF + Excel" };

/** What an owner sets on a scheduled report email (docs/scheduled-reports.md). */
export interface ScheduleBody {
  dashboardId: string;
  /** Null: the whole dashboard. */
  widgetIds: string[] | null;
  format: ScheduleFormat;
  cadence: Cadence;
  timezone: string;
  /** The date range each send covers, from each location's business day at send time. */
  timePreset: TimePreset;
  locationIds: string[];
  /** known_users ids. */
  recipients: string[];
}

/** One send of a schedule. */
export interface ScheduleRun {
  id: string;
  status: "built" | "sent" | "failed";
  error: string | null;
  bytes: number | null;
  rowCount: number | null;
  provisional: boolean;
  recipients: string[] | null;
  attempt: number | null;
  /** The sent email's Message-ID, to find it in Email Sending's activity log. */
  messageId: string | null;
  startedAt: string;
  finishedAt: string | null;
}

export interface Recipient {
  id: string;
  name: string;
  email?: string | null;
  staff: boolean;
  /** An external contact (`contact:<id>`), not someone who signs in here. */
  contact?: boolean;
}

/** An email address staff granted locations to, without it ever signing in (docs/scheduled-reports.md). */
export interface ExternalContact {
  id: string;
  email: string;
  name: string | null;
  locationIds: string[];
  /** The same locations with their names (just the id when it couldn't be named). */
  locations: { id: string; name: string }[];
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface ContactBody {
  email: string;
  name: string | null;
  locationIds: string[];
}

export interface ApprovedDomain {
  domain: string;
  addedBy: string;
  addedAt: string;
}

export interface ContactEvent {
  id: string;
  seq: number;
  kind: "contact_added" | "contact_changed" | "contact_deleted" | "domain_added" | "domain_removed";
  contactId: string | null;
  domain: string | null;
  actorId: string;
  actorEmail: string | null;
  /** Contact events: the contact after (and `before`, for a change). */
  detail: (ContactBody & { before?: ContactBody }) | null;
  at: string;
}

export interface Schedule extends ScheduleBody {
  id: string;
  ownerId: string;
  ownerEmail: string | null;
  owner: { name?: string };
  status: "active" | "paused";
  pausedReason: string | null;
  failureStreak: number;
  nextRunAt: string;
  lastRunAt: string | null;
  createdAt: string;
  updatedAt: string;
  /** Null when the dashboard was deleted. */
  dashboardTitle: string | null;
  people: Recipient[];
  lastRun: ScheduleRun | null;
}

export interface ScheduleDetail extends Schedule {
  runs: ScheduleRun[];
  locations: { id: string; name: string }[];
}

export interface StaffLocation {
  id: string;
  name: string;
  place: string | null;
}

export class ApiError extends Error {
  constructor(readonly status: number, readonly code: string, message: string) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`/api${path}`, {
    ...init,
    credentials: "same-origin",
    headers: { "content-type": "application/json", ...(init.headers ?? {}) },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: { code: string; message: string } } | null;
    throw new ApiError(res.status, body?.error?.code ?? `http_${res.status}`, body?.error?.message ?? res.statusText);
  }
  return res.json() as Promise<T>;
}

const post = <T>(path: string, body: unknown) => request<T>(path, { method: "POST", body: JSON.stringify(body) });
const put = <T>(path: string, body: unknown) => request<T>(path, { method: "PUT", body: JSON.stringify(body) });
const del = <T>(path: string) => request<T>(path, { method: "DELETE" });
const patch = <T>(path: string, body: unknown) => request<T>(path, { method: "PATCH", body: JSON.stringify(body) });

const searchParams = (s: object) =>
  new URLSearchParams(Object.entries(s).filter(([, v]) => v !== undefined && v !== "").map(([k, v]) => [k, String(v)]));

export const api = {
  authConfig: () => request<{ devLogin: boolean; personas: string[]; staffSso: boolean; gotabSso: boolean; gotabLoginRoundTrip: boolean }>("/auth/config"),
  gotabStatus: () => request<{ signedIn: boolean; access: boolean }>("/auth/gotab/status"),
  /** Location picker after staff (cf_sso) or GoTab operator (gotab.io) sign-in. */
  pickerLocations: (via: SignInVia, q: string) => request<StaffLocation[]>(`/auth/${via}/locations?q=${encodeURIComponent(q)}`),
  startPickedSession: (via: SignInVia, locationIds: string[]) => post<{ ok: true; next?: string }>(`/auth/${via}/session`, { locationIds }),
  me: () => request<Me>("/me"),
  /** Staff only (anyone else gets none): the workspaces they can open, each on its own host. */
  workspaces: () => request<{ id: string; name: string; host: string }[]>("/workspaces"),
  viewAsUsers: (q: string) => request<ViewAsUser[]>(`/view-as/users?q=${encodeURIComponent(q)}`),
  startViewAs: (userId: string) => post<{ ok: true }>("/view-as", { userId }),
  enableViewAsEdit: (reason: string) => post<{ ok: true }>("/view-as/edit", { reason }),
  exitViewAs: () => post<{ ok: true }>("/view-as/exit", {}),
  viewAsEvents: (s: ViewAsEventSearch, cursor?: number) =>
    request<{ events: ViewAsEvent[]; next: number | null }>(`/admin/view-as-events?${searchParams({ ...s, cursor })}`),
  downloads: (s: DownloadEventSearch, cursor?: number) =>
    request<{ events: DownloadEvent[]; next: number | null }>(`/admin/downloads?${searchParams({ ...s, cursor })}`),
  recordDownload: (r: DownloadReport) => post<{ ok: true }>("/downloads", r),
  llmUsage: (s: LlmUsageSearch) => request<{ totals: LlmUsageTotal[] }>(`/admin/llm-usage?${searchParams(s)}`),
  askAccessList: () => request<AskAccess[]>("/admin/ask-access"),
  askAccessSearch: (q: string) => request<AskCandidate[]>(`/admin/ask-access/search?q=${encodeURIComponent(q)}`),
  askAccess: (userId: string) => request<AskAccessDetail>(`/admin/ask-access/${encodeURIComponent(userId)}`),
  setAskAccess: (userId: string, enabled: boolean) => put<AskAccess>(`/admin/ask-access/${encodeURIComponent(userId)}`, { enabled }),
  schedules: () => request<Schedule[]>("/admin/schedules"),
  /** Super users: every schedule in the workspace, read-only. */
  allSchedules: () => request<Schedule[]>("/admin/all-schedules"),
  schedule: (id: string) => request<ScheduleDetail>(`/admin/schedules/${id}`),
  scheduleRecipients: (q: string, locationIds: string[]) =>
    request<Recipient[]>(`/admin/schedules/recipients?${new URLSearchParams({ q, locationIds: locationIds.join(",") })}`),
  createSchedule: (body: ScheduleBody) => post<Schedule>("/admin/schedules", body),
  updateSchedule: (id: string, body: ScheduleBody) => put<Schedule>(`/admin/schedules/${id}`, body),
  deleteSchedule: (id: string) => del<{ ok: true }>(`/admin/schedules/${id}`),
  pauseSchedule: (id: string) => post<Schedule>(`/admin/schedules/${id}/pause`, {}),
  resumeSchedule: (id: string) => post<Schedule>(`/admin/schedules/${id}/resume`, {}),
  sendScheduleNow: (id: string) => post<{ runId: string }>(`/admin/schedules/${id}/send`, {}),
  contacts: (q = "") => request<ExternalContact[]>(`/admin/contacts?${searchParams({ q })}`),
  createContact: (body: ContactBody) => post<ExternalContact>("/admin/contacts", body),
  updateContact: (id: string, body: ContactBody) => put<ExternalContact>(`/admin/contacts/${id}`, body),
  deleteContact: (id: string) => del<{ ok: true }>(`/admin/contacts/${id}`),
  approvedDomains: () => request<ApprovedDomain[]>("/admin/approved-domains"),
  /** Super users only, like removing one. */
  approveDomain: (domain: string) => post<{ domain: string }>("/admin/approved-domains", { domain }),
  removeApprovedDomain: (domain: string) => del<{ ok: true }>(`/admin/approved-domains/${encodeURIComponent(domain)}`),
  contactEvents: (contactId?: string, cursor?: number) =>
    request<{ events: ContactEvent[]; next: number | null }>(`/admin/contact-events?${searchParams({ contactId, cursor })}`),
  logout: () => post<{ ok: true }>("/auth/logout", {}),
  // The browser may still hold the single catalog an older release served (cached for 5 minutes);
  // that's one pack, and a one-pack workspace's queries never name it.
  catalog: async (): Promise<WorkspaceCatalog> => {
    const c = await request<WorkspaceCatalog | Catalog>("/catalog");
    return "packs" in c ? c : { packs: [{ ...c, name: "", label: "" }] };
  },
  locations: () => request<{ id: string; name: string }[]>("/locations"),
  query: (query: SemanticQueryInput, locationIds?: string[], origin?: QueryOrigin, dashboardFilters?: DashboardFilter[], inputs?: Record<string, number>) =>
    post<ResultEnvelope>("/query", {
      query, locationIds, origin, dashboardFilters: dashboardFilters?.length ? dashboardFilters : undefined,
      inputs: inputs && Object.keys(inputs).length ? inputs : undefined,
    }),
  explain: (query: SemanticQueryInput, locationIds?: string[]) => post<ExplainResult>("/query/explain", { query, locationIds }),
  /** With `widget`: change it as the question asks. Without: build a new one. */
  nl: (prompt: string, widget?: WidgetDefinitionInput) => post<NlResult | NotAChart>("/nl", { prompt, widget }),
  askUsage: () => request<AskUsage>("/me/ask-usage"),
  dashboards: () => request<DashboardWithMeta[]>("/dashboards"),
  dashboard: (id: string) => request<DashboardDetail>(`/dashboards/${id}`),
  createDashboard: (title: string) => post<DashboardDetail>("/dashboards", { title }),
  /** Fails with a 409 ApiError when `revision` is no longer current. */
  saveDashboard: (id: string, body: DashboardBody, revision: number) => put<DashboardDetail>(`/dashboards/${id}`, { ...body, revision }),
  setShares: (id: string, targets: ShareTarget[]) => put<{ shares: ShareTarget[] }>(`/dashboards/${id}/shares`, { targets }),
  /** Answer a dashboard shared with you; "pending" takes it back out of your sidebar. */
  respondToShare: (id: string, status: ShareStatus) => put<{ status: ShareStatus }>(`/dashboards/${id}/share-response`, { status }),
  /** Your order for one list in your sidebar, top first: a folder of yours, or with no folder, Mine or Shared with me. */
  setSidebarOrder: (ids: string[], folderId?: string) => put<{ ok: true }>("/me/sidebar-order", { ids, folderId }),
  sidebarFolders: () => request<SidebarFolder[]>("/me/sidebar-folders"),
  createSidebarFolder: (name: string) => post<SidebarFolder>("/me/sidebar-folders", { name }),
  renameSidebarFolder: (id: string, name: string) => put<{ ok: true }>(`/me/sidebar-folders/${id}`, { name }),
  /** The folder only; its dashboards go back to Mine or Shared with me. */
  deleteSidebarFolder: (id: string) => del<{ ok: true }>(`/me/sidebar-folders/${id}`),
  setSidebarFolderOrder: (ids: string[]) => put<{ ok: true }>("/me/sidebar-folders/order", { ids }),
  shareDirectory: (q: string, locationId?: string) =>
    request<DirectoryPerson[]>(`/share/directory?${new URLSearchParams({ q, ...(locationId && { locationId }) })}`),
  duplicateDashboard: (id: string) => post<DashboardDetail>(`/dashboards/${id}/duplicate`, {}),
  deleteDashboard: (id: string) => del<{ ok: true }>(`/dashboards/${id}`),
  createWidget: (def: WidgetDefinitionInput) => post<Widget>("/widgets", def),
  updateWidget: (id: string, def: WidgetDefinitionInput) => put<Widget>(`/widgets/${id}`, def),
  duplicateWidget: (id: string, title: string) => post<Widget>(`/widgets/${id}/duplicate`, { title }),
  marketplace: (s: MarketplaceSearch) => request<ListingPage>(`/marketplace?${searchParams(s)}`),
  marketplaceFacets: () => request<MarketplaceFacets>("/marketplace/categories"),
  myListings: () => request<MyListing[]>("/marketplace/mine"),
  allListingStats: () => request<MyListing[]>("/admin/marketplace/stats"),
  listing: (id: string) => request<ListingDetail>(`/marketplace/${id}`),
  publish: (req: PublishRequestInput) => post<ListingDetail>("/marketplace", req),
  install: (id: string, target: InstallRequest["target"]) => post<{ dashboardId: string }>(`/marketplace/${id}/install`, { target }),
  updatePreview: (id: string, target: UpdateTarget) => request<UpdatePreview>(`/marketplace/${id}/update?${new URLSearchParams(target)}`),
  /** Fails with a 409 ApiError when the copy changed since the preview. */
  updateCopy: (id: string, req: UpdateCopyRequest) => post<{ dashboardId?: string }>(`/marketplace/${id}/update`, req),
  like: (id: string, liked: boolean) => (liked ? post<Listing>(`/marketplace/${id}/like`, {}) : del<Listing>(`/marketplace/${id}/like`)),
  unpublish: (id: string) => del<{ ok: true }>(`/marketplace/${id}`),
  moderate: (id: string, change: { status?: Listing["status"]; verified?: boolean; featured?: boolean }) =>
    patch<ListingDetail>(`/admin/marketplace/${id}`, change),
};
